/**
 * Created by miguel on 22/2/17.
 */
/// <reference path="InvoiceReviewState.ts" />

// The detail of an invoice (purchase, sales, licence): the top bar with the reading's review and
// the attached files with their counts, the actions, save and process; the status line in the
// invoice's colour; the header form; the lines with their product link; the new line always at
// the bottom; and the totals with what keeps the invoice from being processed.

class InvoiceDetailViewController extends MUIViewController
{
    static newInstance(): InvoiceDetailViewController {
        let vc = new InvoiceDetailViewController("invoice-detail-view");
        vc.initWithResource("layout/invoices/InvoiceDetailView.html");
        return vc;
    }

    private printButton:MUIButton = null; 
    private actionButton:MUIButton = null;    
    private saveButton:MUIButton = null;
    // The process button of the top bar (a layout may still keep it inside its header views).
    private processButton:MUIButton = null;
    private statusBar:MUIView = null;
    private validationView:MUIView = null;
    private validationLabel:MUILabel = null;

    protected noEditHeader:InvoiceDetailHeaderView = null;
    protected editHeader:InvoiceDetailHeaderView = null;
    
    protected topAddLineView:InvoiceLineCell = null;
    protected bottomAddLineView:InvoiceLineCell = null;
    
    protected addLineView:InvoiceLineCell = null;

    private tableView:UITableView = null;
    
    private commentsTextField: MUITextField   = null;
    private commentsLabel:MUILabel            = null;
    private totalBaseLabel:MUILabel           = null;
    private totalTaxLabel:MUILabel            = null;
    private totalLegalTaxLabel:MUILabel       = null;
    private totalLabel:MUILabel               = null;

    private ad = MUIWebApplication.sharedInstance().delegate;

    private df:MIODateFormatter = this.ad.dateFormatter;
    private cf:MIONumberFormatter = this.ad.currencyFormatter;
    private nf:MIONumberFormatter = this.ad.numberFormatter;
    // Detected lines of a purchase invoice read by DLInsightsBotServer (block in the layout).
    private extractionSession:DocumentExtractionSession = null;
    private extractionButton:MUIButton = null;
    
    
    viewDidLoad(){
        super.viewDidLoad();

        this.printButton = MUIOutlet(this, "print-btn", "MUIButton");
        if (this.printButton) this.printButton.setAction(this, function(this:InvoiceDetailViewController){
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;        
            ad.webService.downloadPDF(this.invoice);
        });

        this.actionButton = MUIOutlet(this, "action-btn", "MUIButton");
        this.actionButton.setAction(this, function(this:InvoiceDetailViewController){
            this.showActions();
        });
        this.setTooltip(this.actionButton, MIOLocalizeString("ACTIONS", "Actions"));

        this.saveButton = MUIOutlet(this, "save-btn", "MUIButton");
        this.saveButton.setAction(this, DBHelper.saveMainContext);

        this.processButton = MUIOutlet(this, "toolbar-process-btn", "MUIButton");
        if (this.processButton) this.processButton.setAction(this, function(this:InvoiceDetailViewController){
            let header = this.editHeader ?? this.noEditHeader;
            if (header != null) header.processButtonDidPress();
        });

        this.statusBar = MUIOutlet(this, "status-bar", "MUIView");
        this.validationView = MUIOutlet(this, "validation-view", "MUIView");
        this.validationLabel = MUIOutlet(this, "validation-lbl", "MUILabel");
        if (this.validationView) this.validationView.hidden = true;
        
        this.editHeader = MUIOutlet(this, "edit-header-view" );
        this.noEditHeader = MUIOutlet(this, "no-edit-header-view" );
        if (this.editHeader) this.editHeader.setLegalEntityAction(this, this.showClientSelectionViewController);

        this.commentsTextField = MUIOutlet( this, "comments-tf", "MUITextField" );
        this.commentsTextField.setOnChangeText( this, function(this:InvoiceDetailViewController, control:MUITextField, value:string) {
            this.invoice.comments = value.trim().length == 0 ? null : value.trim();
        });
        this.commentsLabel = MUIOutlet(this, "comments-lbl", "MUILabel");

        this.tableView             = MUIOutlet(this, 'table-view', 'UITableView');
        this.tableView.dataSource  = this;
        this.tableView.delegate    = this;
        
        this.totalBaseLabel = MUIOutlet(this, 'total-base-lbl','MUILabel');
        this.totalTaxLabel = MUIOutlet(this, 'total-tax-lbl','MUILabel');
        this.totalLegalTaxLabel = MUIOutlet(this, 'total-legal-tax-lbl','MUILabel');
        this.totalLabel = MUIOutlet(this, 'total-amount-lbl','MUILabel');

        this.topAddLineView = MUIOutlet(this, "top-add-line");
        if (this.topAddLineView) {
            this.topAddLineView.delegate = this;
            this.topAddLineView.hidden = true;
        }

        this.bottomAddLineView = MUIOutlet(this, "bottom-add-line");
        if (this.bottomAddLineView) {
            this.bottomAddLineView.delegate = this;
            this.bottomAddLineView.hidden = true;
        }

        this.addLineView = SettingsHelper.sharedInstance().configStockUIAddLineTop ? this.topAddLineView : this.bottomAddLineView;

    }

    viewWillAppear(animate?){
        super.viewWillAppear(animate);                
        this.updateUI();
    }
    
    protected invoice:Invoice = null;
    set item(value:Invoice){
        if (this.invoice != null) this.invoice.removeObserver(this, "status");
        this.invoice = value;   
        if (this.invoice != null) this.invoice.addObserver(this, "status", null);
        this.updateUI();
    }

    private ldtf = MUIWebApplication.sharedInstance().delegate.longDateTimeFormatter as MIODateFormatter;
    protected updateUI(){
        if(!this.viewIsLoaded || this.invoice == null) return;

        let processed = this.invoice.status == DBDocumentStatus.processed;

        // once processed the lines have no icon column: they start on the cell padding
        let root = this.view.layer as HTMLElement;
        if (processed) root.classList.add("inv-processed"); else root.classList.remove("inv-processed");

        this.editHeader.document = this.invoice;
        this.editHeader.hidden = processed;
        this.noEditHeader.document = this.invoice;
        this.noEditHeader.hidden = !processed;

        this.commentsTextField.text = this.invoice.comments;
        this.updateComments(processed);
        this.updateProcessButton();

        this.fetchedResultsController = null;
        this.tableView.reloadData();    
        this.updateTotals();
        
        if (this.addLineView) {
            this.addLineView.invoice = this.invoice;
            this.addLineView.hidden = processed;
        }
        this.updateDefaultLegalTax();

        if (this.invoice instanceof PurchaseInvoice) {
            if (this.extractionSession == null) {
                this.extractionSession = DocumentExtractionSession.attach(this, this.invoice, this);
                // the same button the session shows and hides with the reading
                this.extractionButton = MUIOutlet(this, "extraction-info-btn", "MUIButton");
            }
            else this.extractionSession.setDocument(this.invoice);
            this.updateExtractionBadge();
        }
        else if (this.extractionSession != null) this.extractionSession.hide();
    }

    // The retention the customer or the supplier of the invoice has in its info is the one a new
    // line starts with. It is read with the entity, as the invoice only brings the entity itself.
    private updateDefaultLegalTax(){
        if (this.addLineView == null) return;

        let invoice = this.invoice;
        let entity = invoice != null ? invoice.legalEntity : null;
        if (entity == null) { this.addLineView.defaultLegalTax = null; return; }

        let predicate = MIOPredicate.predicateWithFormat("identifier == " + entity.identifier);
        DBHelper.queryObjectWithCompletion("LegalEntity", null, predicate, ["legalTax"], this, function(this:InvoiceDetailViewController, object:LegalEntity){
            // another invoice, or another entity for this one, was shown meanwhile
            if (this.invoice !== invoice || this.invoice.legalEntity !== entity) return;
            this.addLineView.defaultLegalTax = object != null ? object.legalTax : null;
        });
    }

    // The comments: a field while the invoice can be edited, text once it is processed.
    private updateComments(processed:boolean){
        if (this.commentsLabel == null) { this.commentsTextField.hidden = false; return; }
        this.commentsTextField.hidden = processed;
        this.commentsLabel.hidden = !processed;
        this.commentsLabel.text = DocumentExtractionLineCell.escape(this.invoice.comments ?? "");
    }

    // PROCESS while the invoice can be processed, PROCESSED (in red, it unprocesses) once it is.
    private updateProcessButton(){
        if (this.processButton == null) return;
        let processed = this.invoice.status == DBDocumentStatus.processed;
        this.processButton.title = processed ? MIOLocalizeString("PROCESSED", "PROCESSED") : MIOLocalizeString("PROCESS", "PROCESS");
        let layer = this.processButton.layer as HTMLElement;
        if (processed) layer.classList.add("processed"); else layer.classList.remove("processed");
    }

    // A count on an icon of the top bar (the reading's warnings, the attached files); none when zero.
    protected setBadge(button:MUIButton, count:number, warning:boolean, error:boolean = false){
        if (button == null) return;
        let layer = button.layer as HTMLElement;
        let badge:HTMLElement = null;
        for (let i = 0; i < layer.children.length; i++) {
            let child = layer.children[i] as HTMLElement;
            if (child.classList.contains("badge")) { badge = child; break; }
        }
        if (count == null || count <= 0) { if (badge != null) layer.removeChild(badge); return; }
        if (badge == null) {
            badge = document.createElement("div");
            badge.className = "badge";
            layer.appendChild(badge);
        }
        badge.textContent = count > 99 ? "99+" : String(count);
        if (warning) badge.classList.add("warning"); else badge.classList.remove("warning");
        if (error) badge.classList.add("error"); else badge.classList.remove("error");
    }

    protected setTooltip(button:MUIButton, text:string){
        if (button == null) return;
        (button.layer as HTMLElement).title = text ?? "";
    }

    // The reading's warnings, on the icon that opens the recognised document.
    private updateExtractionBadge(){
        if (this.extractionButton == null || this.extractionSession == null) return;
        let state = this.extractionSession.state;
        let count = (state == "extracted" || state == "empty") ? this.extractionSession.warningTexts().length : 0;
        this.setBadge(this.extractionButton, count, true, this.extractionSession.issueLevel == "error");
        this.setTooltip(this.extractionButton, MIOLocalizeString("AI_EXTRACTION_REVIEW", "AI extraction review") + (count > 0 ? " · " + count + " " + MIOLocalizeString("WARNINGS", "warnings") : ""));
    }

    // DocumentExtractionSessionDelegate
    extractionSessionDidInsertLine(session:DocumentExtractionSession, line:any) {
        this.calculateTotals();
        DBHelper.saveMainContext();
    }

    extractionSessionDidFinish(session:DocumentExtractionSession) {
        this.updateUI();
    }

    extractionSessionDidChange(session:DocumentExtractionSession) {
        this.updateReviewState();
    }

    observeValueForKeyPath(keypath:string, type:string, object:any){

        if (type == "did") {
            switch (keypath) {
                case "status": this.updateUI(); break;
            }
        }
    }

    numberOfSections(tableView:UITableView){
        return this.fetchedResultsController.sections.length;
    }
    
    numberOfRowsInSection(tableView:UITableView, section:number){
        let sec = this.fetchedResultsController.sections[section];
        return sec.numberOfObjects();
    }

    // titleForHeaderInSection(tableView:UITableView, section:number) {
    //     let ip = MIOIndexPath.indexForRowInSection(0, section);
    //     let item = this.fetchedResultsController.objectAtIndexPath(ip); 
    //     if (item.wasCloned == true) return MIOLocalizeString("Previous documents".toUpperCase(), "Previous documents")
    //     else if(item.wasCloned == false) return MIOLocalizeString("Current documents".toUpperCase(), "Current documents")
    //     else return "";
    // }
    
    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){
        let item = this.fetchedResultsController.objectAtIndexPath( indexPath ) as DBDocumentLine;

        let cell:InvoiceLineCell = null;
        if ( item instanceof DBDocumentTitleLine ) {
            cell = tableview.dequeueReusableCellWithIdentifier( 'TitleLineCell' ) as InvoiceLineCell;
        }
        // else if ( item instanceof InvoiceLine ) {
        else {
            let cellIdentifier = this.invoice.status == DBDocumentStatus.processed ? "LineCell" : "EditLineCell"
            cell = tableview.dequeueReusableCellWithIdentifier( cellIdentifier ) as InvoiceLineCell;
            cell.delegate = this;
        }
                
        cell.invoice = this.invoice;
        cell.underReview = this.isUnderReview;
        cell.item = item;
                    
        return cell;
    }

    // A purchase invoice whose document is being read or whose reading is being gone through:
    // the lines then show the product link field with the printed text under it.
    private get isUnderReview() : boolean {
        if (!(this.invoice instanceof PurchaseInvoice)) return false;
        let status = this.invoice.ocrStatus;
        return status == DBDocumentOCRStatus.extracted || status == DBDocumentOCRStatus.scanning;
    }

    // The lines can be deleted while the invoice can be edited, not once it is processed.
    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) {
        if (this.invoice != null && this.invoice.status == DBDocumentStatus.processed) return UITableViewCellEditingStyle.None;
        return UITableViewCellEditingStyle.Delete;
    }
    
    commitEditingStyleForRowAtIndexPath(tableView, editingStyle:UITableViewCellEditingStyle, indexPath:MIOIndexPath) {

        let line = this.fetchedResultsController.objectAtIndexPath(indexPath);    

        if (editingStyle == UITableViewCellEditingStyle.Delete) {
            DBHelper.deleteObjectFromMainContext(line, true);
            this.lineDidRemove(line);
        }
    }
    private _fetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null)
            this._fetchedResultsController.delegate = null;
    
        this._fetchedResultsController = value;
    }
    
    get fetchedResultsController(){
        if (this._fetchedResultsController != null)
            return this._fetchedResultsController;
    
        let ad = MUIWebApplication.sharedInstance().delegate;
    
        let sds = [ MIOSortDescriptor.sortDescriptorWithKey('orderIndex', true), MIOSortDescriptor.sortDescriptorWithKey('date', true) ];
        let fetchRequest = DBHelper.listFetchRequestWithEntityName("DBDocumentLine", sds, "document.identifier == " + this.invoice.identifier );
        fetchRequest.relationshipKeyPathsForPrefetching = ["tax", "legalTax", "product"];
        
        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;
    
        fetchedResultsController.performFetch();
    
        this._fetchedResultsController = fetchedResultsController;
    
        return this._fetchedResultsController;
    }
    
    controllerDidChangeContent(controller){
        this.tableView.reloadData();   
        
        // Calculate
        this.updateTotals();
    }

    private showActions(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("CHOOSE_AN_ACTION", "CHOOSE AN ACTION"), null, MUIAlertViewStyle.Default);

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("ADD_PAYMENT", "ADD PAYMENT"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
            // MIONotificationCenter.defaultCenter().postNotification("InvoiceWillDelete", this.invoice);
            // DBHelper.deleteObjectFromMainContext(this.invoice, true);            
        }));    

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CHANGE_CLIENT", "CHANGE CLIENT"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
			this.showClientSelectionViewController();
        }));

        if ( this.invoice instanceof PurchaseInvoice || (MUIWebApplication.sharedInstance().delegate as AppDelegate).selectedUser.isAdmin ) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("DELETE_INVOICE", "DELETE INVOICE"), MUIAlertActionStyle.Destructive, this, function(this:InvoiceDetailViewController){
                MIONotificationCenter.defaultCenter().postNotification("InvoiceWillDelete", this.invoice);
                DBHelper.deleteObjectFromMainContext(this.invoice, true);                
            }));
        }
        
        if ( !(this.invoice instanceof PurchaseInvoice) ) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("VOID_INVOICE", "VOID INVOICE"), MUIAlertActionStyle.Destructive, this, function(this:InvoiceDetailViewController){
                let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
                ad.webService.nullifyInvoice( this.invoice, (data:any, error:string) => {
                    //TODO: Show next invoice number in the UI
                });
            }));
        }

        if (this.invoice instanceof LicenseInvoice && (MUIWebApplication.sharedInstance().delegate as AppDelegate).selectedUser.isAdmin) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CHANGE_FEE_AMOUNT", "CHANGE FEE AMOUNT"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
                this.showFeeAlert();
            }));    
        }

        avc.addAction( MUIAlertAction.alertActionWithTitle(MIOLocalizeString("SEND","SEND"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            ad.webService.sendLicenseInvoice( this.invoice, null );
        }));

        if ( this.invoice instanceof PurchaseInvoice ) {
            avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("PROCESS_OCR", "PROCESS OCR"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
                let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
                ad.webService.scanInvoiceByOCR(this.invoice, function(code:number, data:any){
                    if (code == 200) {
                        AppHelper.showInfoMessage(null, MIOLocalizeString("SUCCESS", "SUCCESS"), MIOLocalizeString("OCR_PROCESSED_SUCCESSFULLY", "OCR processed successfully."));
                    } else {
                        AppHelper.showErrorMessage(null, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("COULDNT_PROCESS_OCR", "Couldn't process OCR. Please try again."));
                    }
                });
            }));
        }

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString("REGENERATE_JOURNAL_ENTRIES", "REGENERATE JOURNAL ENTRIES"), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            let templateType = this.invoice instanceof PurchaseInvoice ? "purchase-invoice" : "sales-invoice";
            ad.webService.regenerateJournalEntries( this.invoice, templateType, null );
        }));

        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString('CANCEL', 'CANCEL'), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    private showFeeAlert(){
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("CHANGE_FEE_AMOUNT", "CHANGE FEE AMOUNT"), null, MUIAlertViewStyle.Default);
        
        avc.addTextFieldWithConfigurationHandler(this, function(this:InvoiceDetailViewController, textField:MUITextField){
            textField.placeHolder = MIOLocalizeString('AMOUNT', 'AMOUNT');
            // textField.keyboardType = MUITextFieldKeyboardType.NumberPad;
            textField.text = this.nf.stringFromNumber((this.invoice as any).totalDistributorFeeAmount);
        });
        
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString('OK', 'OK'), MUIAlertActionStyle.Default, this, function(this:InvoiceDetailViewController){
            let amount = avc.textFields[0].text;
            (this.invoice as any).totalDistributorFeeAmount = this.nf.numberFromString(amount);
            DBHelper.saveMainContext();
        }));
        avc.addAction(MUIAlertAction.alertActionWithTitle(MIOLocalizeString('CANCEL', 'CANCEL'), MUIAlertActionStyle.Cancel, null, null));

        this.presentViewController(avc, true);
    }

    //
    // Line delegate
    //
    lineDidRemove(line:PurchaseInvoiceLine) {
        this.calculateTotals();
    }

    lineDidInserted(line:PurchaseInvoiceLine) {
        if (this.currentSectionLine) this.currentSectionLine.addLinesObject(line);
        this.calculateTotals();
    }

    lineDidUpdate(line:PurchaseInvoiceLine) {
        this.calculateTotals();
    }

    private currentSectionLine:DBDocumentTitleLine = null;
    lineSectionDidInsert(section:DBDocumentTitleLine) {
        this.currentSectionLine = section;
    }

    // Calculate totals
    private calculateTotals() {
        let total = 0;
        let tax = 0;
        let legal_tax = 0;
        let discount = 0;
        let base = 0;

        for (let i =0; i < this.fetchedResultsController.fetchedObjects.count; i++){
            let l = this.fetchedResultsController.fetchedObjects.objectAtIndex(i);
            if ( l instanceof InvoiceLine) {
                // base += l.baseAmount;
                base += l.baseAmount
                tax += l.vatAmount;
                legal_tax += l.legalTaxAmount;
                discount += l.discountAmount;
                total += l.totalAmount;                
            }
        }

        this.invoice.baseAmount = base;        
        this.invoice.vatAmount = tax;
        this.invoice.taxesAmount = legal_tax;
        this.invoice.discountAmount = discount;
        this.invoice.totalAmount = total;

        this.updateTotals();
    }

    updateTotals(){
        this.totalBaseLabel.text = this.cf.stringFromNumber(this.invoice.baseAmount);
        this.totalTaxLabel.text  = this.cf.stringFromNumber(this.invoice.vatAmount);
        this.totalLabel.text     = this.cf.stringFromNumber(this.invoice.totalAmount);
        this.totalLegalTaxLabel.text = this.cf.stringFromNumber(this.invoice.taxesAmount);
        this.updateReviewState();
    }

    // The lines the view shows (the fetched ones), for the checks.
    private get shownLines() : any[] {
        let objects = this.fetchedResultsController != null ? this.fetchedResultsController.fetchedObjects : null;
        let lines:any[] = [];
        if (objects == null) return lines;
        for (let i = 0; i < objects.count; i++) lines.push(objects.objectAtIndex(i));
        return lines;
    }

    // What keeps the invoice from being processed, for the status line, the summary next to the
    // totals and the totals themselves (red meanwhile): a line without product or concept item or
    // without VAT, detected lines still to add, a reading that failed, or lines that do not add up
    // to the total read on the document. The list cell's stripe is told, so it shows the same colour.
    private updateReviewState(){
        if (this.invoice == null) return;
        let lines = this.shownLines;
        let parts:string[] = [];

        // the detected lines take the free space until the invoice has lines of its own
        let root = this.view.layer as HTMLElement;
        if (lines.length > 0) root.classList.add("inv-has-lines"); else root.classList.remove("inv-has-lines");

        let counts = InvoiceDetailHeaderView.incompleteCounts(this.invoice, lines);
        if (counts.noProduct > 0) parts.push(counts.noProduct + " " + (counts.noProduct == 1 ? MIOLocalizeString("LINE_WITHOUT_PRODUCT", "line without product") : MIOLocalizeString("LINES_WITHOUT_PRODUCT", "lines without product")));
        if (counts.noVat > 0) parts.push(counts.noVat + " " + MIOLocalizeString("WITHOUT_VAT", "without VAT"));

        let session = this.extractionSession;
        if (session != null && this.invoice instanceof PurchaseInvoice) {
            let pending = session.pendingRowCount;
            if (pending > 0) parts.push(pending + " " + (pending == 1 ? MIOLocalizeString("DETECTED_LINE_TO_ADD", "detected line to add") : MIOLocalizeString("DETECTED_LINES_TO_ADD", "detected lines to add")));
            if (session.state == "failed") parts.push(MIOLocalizeString("READING_FAILED", "the reading failed"));
            let readTotal = session.readTotal;
            if (readTotal != null) {
                let linesTotal = 0;
                for (let l of lines) if (l instanceof InvoiceLine) linesTotal += Number(l.totalAmount ?? 0);
                if (Math.abs(linesTotal - readTotal) >= 0.015) parts.push(MIOLocalizeString("LINES_DO_NOT_ADD_UP_TO_THE_TOTAL_READ", "the lines do not add up to the total read") + " (" + this.cf.stringFromNumber(readTotal) + ")");
            }
        }

        let processed = this.invoice.status == DBDocumentStatus.processed;
        let issues = parts.length > 0 && !processed;
        this.invoice.reviewHasIssues = parts.length > 0;

        for (let label of [this.totalBaseLabel, this.totalTaxLabel, this.totalLegalTaxLabel, this.totalLabel]) {
            if (label == null) continue;
            let layer = label.layer as HTMLElement;
            if (issues) layer.classList.add("totals-error"); else layer.classList.remove("totals-error");
        }
        if (this.validationLabel) this.validationLabel.text = parts.join(" · ");
        if (this.validationView) this.validationView.hidden = !issues;

        let state = InvoiceReviewStateHelper.stateForInvoice(this.invoice);
        if (this.statusBar) InvoiceReviewStateHelper.applyStateToLayer(this.statusBar.layer as HTMLElement, state, "", true);
        MIONotificationCenter.defaultCenter().postNotification(InvoiceReviewStateHelper.didChangeNotification, this.invoice);

        this.updateExtractionBadge();
    }

    protected showClientSelectionViewController() {
        let vc = LegalEntitySelectionViewController.newInstance();
        vc.defaultSelectionType = this.invoice instanceof PurchaseInvoice ? LegalEntityDefaultSelectionType.supplier : LegalEntityDefaultSelectionType.client;
        vc.completionBlock = (item:LegalEntity) => {
            this.changeClient( item );
        };
        
        this.presentViewController(vc, true);
    }

    private changeClient( client:LegalEntity ){
        let p = MIOPredicate.predicateWithFormat( "deletedAt = null && next = null && status = 0 && invoice = " + this.invoice.identifier );
        DBHelper.queryObjectsWithCompletion( "LegalEntityPaymentInstallment", null, p, [], this, function(this:InvoiceDetailViewController, installments:LegalEntityPaymentInstallment[]) {
            for (let i = 0; i < installments.length; i++){
                let item = installments[i];
                item.legalEntity = client;
                item.legalEntityName = client.name;
            }
            this.invoice.legalEntity = client;            
            DBHelper.saveMainContext();
            this.updateUI();    
        });        
    }

}
